import { handleAxiosError } from '@/api/helpers';
import { FiltersType } from '@/api/types';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { client } from '@/client';
import { Account } from '@/client/accounts';
import { CampaignStatusesEnum } from '@/client/campaigns';
import { FilterNamesEnum } from '@/client/helpers';
import {
  LoadingStateType,
  LoadingStatuses,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import { TranslationFunctionType } from '@/common/types';
import { CampaignReportsDatatable } from '@/components/campaigns/datatables';
import { CampaignsReports } from '@/components/dashboards/admin';
import { DataTableFiltersHandle } from '@/components/tables/crud';
import { useNotifications } from '@/hooks/notifications.hook';
import { useCampaign } from '@/hooks/query';
import { useMasterTemplateReport } from '@/hooks/query/master-template-reports.hooks';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { LoadingPage } from '@/pages';
import { selectCurrentAccount } from '@/store/features/account';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { AxiosError } from 'axios';
import { MenuItem } from 'primereact/menuitem';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

const StyledLegendContainer = styled(FlexContainer)`
  margin-top: var(--default-padding);

  > div {
    display: flex;
    gap: var(--xxsmall-padding);
    font-size: var(--small-font-size);
    user-select: none;
  }
`;

const StyledDot = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;

  &.red {
    background-color: var(--teal-main);
  }

  &.red-light {
    background-color: var(--teal-light);
  }
`;

const getBreadcrumbs = (
  account: Account,
  reportUrl: string,
  t: TranslationFunctionType,
): MenuItem[] => {
  return [
    {
      label: account?.name,
      url: !account?.isSystem
        ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
        : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
      template: AppBreadCrumbTemplate,
    },
    {
      label: t('campaigns'),
      url: RedirectPaths[RedirectPathsEnum.CAMPAIGNS](),
      template: AppBreadCrumbTemplate,
    },
    {
      label: t('generic.campaign.report'),
      url: reportUrl,
      template: AppBreadCrumbTemplate,
    },
  ];
};

export const CampaignsReportPage: React.FC = () => {
  const { t } = useTranslation();
  const { id, masterTemplateId } = useParams();
  const masterMode = !!masterTemplateId;
  const toast = useToast();
  const navigate = useNavigate();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const [syncState, setSyncState] = useState<LoadingStateType>(
    LoadingStatuses.IDLE,
  );
  const [apiFilters, setApiFilters] = useState<FiltersType>([]);
  const { isLoading, campaign, refetch } = useCampaign(id, {
    filters: apiFilters,
    keepPreviousData: true,
    ...(masterMode && { enabled: false }),
  });
  const {
    isLoading: isMasterReportLoading,
    isError: isMasterReportError,
    masterTemplateReport,
    refetch: refetchMasterReport,
  } = useMasterTemplateReport(currentAccount?.id, masterTemplateId, {
    filters: apiFilters,
    keepPreviousData: true,
    enabled: masterMode,
    onError: (error) => {
      if ((error as { statusCode?: number })?.statusCode === 404) {
        navigate(RedirectPaths[RedirectPathsEnum.NOT_FOUND]());
        return;
      }
      handleAxiosError(error as Error | AxiosError, toast);
    },
  });

  const [shouldRefetchUsers, setShouldRefetchUsers] = useState(false);

  const toggleShouldRefetchUsers = () =>
    setShouldRefetchUsers((state) => !state);

  const {
    lastMessage,
    setNotificationParam: setCampaignId,
    notificationParam,
    isConnected,
  } = useNotifications(client.campaigns.campaignsNotifyUrl);

  useEffect(() => {
    if (masterMode || !notificationParam) return;
    handleNotifyCampaignRefresh(lastMessage);
  }, [JSON.stringify(lastMessage)]);

  const handleNotifyCampaignRefresh = async (messages: any) => {
    if (
      !messages?.data?.event ||
      ![
        'campaign.results.sync.finished',
        'campaign.results.sync.failed',
      ].includes(messages?.data?.event)
    )
      return;

    if (messages?.data?.event === 'campaign.results.sync.finished') {
      toast?.success(t('toast.success'), t('campaign.report.refresh.success'));
      await refetch();
      toggleShouldRefetchUsers();
    }

    if (messages?.data?.event === 'campaign.results.sync.failed') {
      toast?.error(t('toast.error'), t('campaign.report.refresh.error'), 5000);
    }

    setCampaignId(undefined);
    setSyncState(LoadingStatuses.IDLE);
  };

  const handleRefreshReports = async () => {
    try {
      setCampaignId(campaign?.id);
      // Wait for a notify connection to establish
      await new Promise((resolve) => {
        const intervalId = setInterval(() => {
          if (isConnected.current) {
            clearInterval(intervalId);
            resolve(true);
          }
        }, 100);
      });

      setSyncState(LoadingStatuses.LOADING);
      await client.campaigns.syncCampaignResults(campaign?.id as string);
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const filtersRef = useRef<DataTableFiltersHandle>(null);

  const STATUS_CIRCLE_IDS = [
    'email-sent',
    'email-opened',
    'clicked-link',
    'submitted-data',
  ];

  const CUMULATIVE_STATUSES: Record<string, string[]> = {
    'email-sent': [
      'email-sent',
      'email-opened',
      'clicked-link',
      'submitted-data',
    ],
    'email-opened': ['email-opened', 'clicked-link', 'submitted-data'],
    'clicked-link': ['clicked-link', 'submitted-data'],
    'submitted-data': ['submitted-data'],
  };

  const handleCircleClick = useCallback((circleId: string) => {
    if (!filtersRef.current) return;

    if (circleId === 'email-reported') {
      const currentValue = filtersRef.current.getFilterValue(
        FilterNamesEnum.EMAIL_REPORTED,
      );
      if (currentValue === 'yes') {
        filtersRef.current.removeFilter(FilterNamesEnum.EMAIL_REPORTED);
      } else {
        filtersRef.current.setFilterValue(
          FilterNamesEnum.EMAIL_REPORTED,
          'yes',
        );
      }
    } else if (STATUS_CIRCLE_IDS.includes(circleId)) {
      const cumulativeSet = CUMULATIVE_STATUSES[circleId] ?? [circleId];
      const currentValues = (
        (filtersRef.current.getFilterValue(
          FilterNamesEnum.CAMPAIGNS_REPORT_BY_STATUS,
        ) as string[]) ?? []
      ).flat();

      const isActive =
        cumulativeSet.length === currentValues.length &&
        cumulativeSet.every((s) => currentValues.includes(s));

      if (isActive) {
        filtersRef.current.removeFilter(
          FilterNamesEnum.CAMPAIGNS_REPORT_BY_STATUS,
        );
      } else {
        filtersRef.current.setFilterValue(
          FilterNamesEnum.CAMPAIGNS_REPORT_BY_STATUS,
          cumulativeSet,
        );
      }
    }
  }, []);

  const handleDatatableFiltersChange = (filters: FiltersType) => {
    setApiFilters(filters);
  };

  if (masterMode) {
    if (
      !masterTemplateReport &&
      isMasterReportError &&
      !isMasterReportLoading
    ) {
      return (
        <FlexContainer direction="column" align="flex-start" gap={16}>
          <AppBreadCrumb
            model={getBreadcrumbs(
              currentAccount as Account,
              RedirectPaths[RedirectPathsEnum.CAMPAIGNS_MASTER_TEMPLATE_REPORT](
                masterTemplateId,
              ),
              t,
            )}
          />
          <p className="m-0">{t('campaign.report.master.loadFailed')}</p>
          <AppButton
            label={t('campaign.report.master.tryAgain')}
            type="outlined"
            onClick={() => refetchMasterReport()}
          />
        </FlexContainer>
      );
    }

    if (!masterTemplateReport || isMasterReportLoading) {
      return <LoadingPage message={t('camapign.report.load')} />;
    }

    return (
      <>
        <AppBreadCrumb
          model={getBreadcrumbs(
            currentAccount as Account,
            RedirectPaths[RedirectPathsEnum.CAMPAIGNS_MASTER_TEMPLATE_REPORT](
              masterTemplateId,
            ),
            t,
          )}
        />
        <FlexContainer justify="space-between">
          <h1 className="m-0">
            {masterTemplateReport.name} - {t('generic.campaign.report')}
          </h1>
        </FlexContainer>

        {!!masterTemplateReport.benchmarkSummary && (
          <StyledLegendContainer justify="flex-end" gap={18}>
            <div>
              <StyledDot className="red" />
              {t('campaign.benchmark.your.campaign')}
            </div>
            <div>
              <StyledDot className="red-light" />
              {t('campaign.benchmark.other.organisations')}
            </div>
          </StyledLegendContainer>
        )}

        <CampaignsReports
          campaign={{
            benchmarkSummary:
              masterTemplateReport.benchmarkSummary ?? undefined,
          }}
          campaignsSummary={masterTemplateReport.summary}
          isLoading={isMasterReportLoading}
          isBenchmark={!!masterTemplateReport.benchmarkSummary}
          t={t}
          className="my-4"
          isTealColor
          onCircleClick={handleCircleClick}
        />

        <CampaignReportsDatatable
          source={{
            type: 'master-template',
            accountId: currentAccount?.id as string,
            masterTemplateId,
          }}
          hideExport
          showRunColumns
          onShouldRefetchChange={toggleShouldRefetchUsers}
          shouldRefetch={shouldRefetchUsers}
          withToolbar
          onFiltersChange={handleDatatableFiltersChange}
          filtersRef={filtersRef}
        />
      </>
    );
  }

  if (!campaign || isLoading) {
    return <LoadingPage message={t('camapign.report.load')} />;
  }

  return (
    <>
      <AppBreadCrumb
        model={getBreadcrumbs(
          currentAccount as Account,
          RedirectPaths[RedirectPathsEnum.CAMPAIGNS_REPORT](campaign.id),
          t,
        )}
      />
      <FlexContainer justify="space-between">
        <h1 className="m-0">
          {campaign?.name} - {t('generic.campaign.report')}
        </h1>
        {campaign?.status &&
          [
            CampaignStatusesEnum.ACTIVE,
            CampaignStatusesEnum.SCHEDULED,
          ].includes(campaign.status) && (
            <AppButton
              label={t('campaign.report.refresh')}
              type="outlined"
              onClick={handleRefreshReports}
              state={syncState}
            />
          )}
      </FlexContainer>

      {!!campaign?.benchmarkSummary && (
        <StyledLegendContainer justify="flex-end" gap={18}>
          <div>
            <StyledDot className="red" />
            {t('campaign.benchmark.your.campaign')}
          </div>
          <div>
            <StyledDot className="red-light" />
            {t('campaign.benchmark.other.organisations')}
          </div>
        </StyledLegendContainer>
      )}

      <CampaignsReports
        campaign={campaign}
        campaignsSummary={campaign.summary}
        isLoading={isLoading}
        isBenchmark={!!campaign?.benchmarkSummary}
        t={t}
        className="my-4"
        isTealColor
        onCircleClick={handleCircleClick}
      />

      <CampaignReportsDatatable
        onShouldRefetchChange={toggleShouldRefetchUsers}
        shouldRefetch={shouldRefetchUsers}
        campaign={campaign}
        withToolbar
        onFiltersChange={handleDatatableFiltersChange}
        filtersRef={filtersRef}
      />
    </>
  );
};
